import { useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { useAgGridStatePersistence, useTheme } from '@circuitry-ai/doc-ui';
import { getExplainerGridStyle } from './explainerGridStyle';

interface PartsCatalogTableProps {
    headers: string[];
    rows: any[][];
    title?: string;
    description?: string;
}

const ExplainerPartsCatalogTable = ({ headers, rows, title, description }: PartsCatalogTableProps) => {
    const { isDark } = useTheme();
    const gridStyle = useMemo(() => getExplainerGridStyle(isDark), [isDark]);

    const paginationPageSizeSelector = useMemo(() => {
        return [10, 20, 40, 50];
    }, []);

    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            filter: 'agTextColumnFilter',
            filterParams: {
                filterOptions: ['contains'],
                defaultOption: 'contains',
                debounceMs: 200,
            },
        }),
        []
    );

    const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
      useAgGridStatePersistence('explainer-parts-catalog-table');

    const onGridReady = useCallback((params: GridReadyEvent) => {
        augmentGridReady(params, (p) => {
            p.api.sizeColumnsToFit?.();
        });
    }, [augmentGridReady]);

    // Convert headers and rows data to AG Grid format
    const rowData = useMemo(() => {
        if (!headers || !rows) return [];

        return rows.map((row, index) => {
            const rowObject: any = { id: index };
            headers.forEach((header, headerIndex) => {
                rowObject[header] = row[headerIndex];
            });
            return rowObject;
        });
    }, [headers, rows]);

    // Create column definitions from headers
    const columnDefs: ColDef[] = useMemo(() => {
        if (!headers) return [];

        return headers.map((header) => ({
            headerName: header.charAt(0).toUpperCase() + header.slice(1).replace(/([A-Z])/g, ' $1'),
            field: header,
            sortable: true,
            cellStyle: { fontSize: '14px', color: 'inherit' },
            minWidth: 150,
            flex: 1,
            wrapText: true,
            autoHeight: false,
            // Special handling for score fields
            ...(header.toLowerCase().includes('score') && {
                cellRenderer: (params: any) => {
                    const value = params.value;
                    return typeof value === 'number' ? value.toFixed(4) : value;
                }
            }),
            // Special handling for description field
            ...(header.toLowerCase().includes('description') && {
                minWidth: 300,
                tooltipField: header,
                cellRenderer: (params: any) => {
                    const value = params.value;
                    if (typeof value === 'string' && value.length > 50) {
                        return (
                            <div title={value}>
                                {value.substring(0, 50)}...
                            </div>
                        );
                    }
                    return value;
                }
            })
        }));
    }, [headers]);

    const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

    if (!headers || !rows || rows.length === 0) {
        return (
            <div className="flex justify-center items-center h-32 text-default-500">
                No data available
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {(title || description) && (
                <div className="flex flex-col">
                    {title && <div className="font-medium text-lg">{title}</div>}
                    {description && <div className="text-sm font-normal mt-1">{description}</div>}
                </div>
            )}
            <div className={'ag-theme-alpine mt-2'} style={gridStyle}>
                <AgGridReact
                    suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
                    rowData={rowData}
                    columnDefs={columnDefsWithSort}
                    defaultColDef={{
                        ...defaultColDef,
                        floatingFilter: true, // This adds the filter input below headers
                    }}
                    headerHeight={40}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    pagination={true}
                    paginationPageSize={10}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    alwaysShowHorizontalScroll={true}
                />
            </div>
        </div>
    );
};

export default ExplainerPartsCatalogTable; 